import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import remarkGfm from "remark-gfm";
import { useLanguageContext } from "../../i18n/LanguageContext";
import publicAgentService, {
  PublicAgentPage,
  PublicChatMessage,
} from "../../features/ai_chatbots/publicAgentService";
import "./AgentPublicPage.scss";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

const FALLBACK_PRIMARY = "#667eea";
const MEYLO_LOGO_WHITE = "/meylo-logo-white.svg";
const MEYLO_LOGO_DARK = "/meylo-logo-dark.svg";

const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", short: "EN", flag: "🇬🇧" },
  { code: "tr", label: "Türkçe", short: "TR", flag: "🇹🇷" },
];

const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = (hex || "").trim().replace("#", "");
  if (normalized.length !== 3 && normalized.length !== 6) return hex;
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getWidgetColors = (settings: any) => {
  const launcher = settings?.launcher || {};
  const windowCfg = settings?.window || {};
  const headerCfg = windowCfg.header || {};
  const messageCfg = windowCfg.message || {};
  const inputCfg = windowCfg.input || {};
  const sendCfg = windowCfg.sendButton || {};

  const primary = launcher.primaryColor || headerCfg.background || FALLBACK_PRIMARY;

  return {
    primary,
    primarySoft: hexToRgba(primary, 0.12),
    primaryGlow: hexToRgba(primary, 0.35),
    onPrimary: headerCfg.titleColor || "#ffffff",
    userBg: messageCfg.userBubble?.bg || "#eef2ff",
    userText: messageCfg.userBubble?.text || "#111827",
    botBg: messageCfg.botBubble?.bg || "#f1f5f9",
    botText: messageCfg.botBubble?.text || "#111827",
    inputBg: inputCfg.bg || "#ffffff",
    inputText: inputCfg.text || "#111827",
    inputBorder: inputCfg.border || "#e5e7eb",
    sendBg: sendCfg.bg || primary,
    sendText: sendCfg.text || "#ffffff",
  };
};

const HTML_TAG_REGEX = /<[a-z][\s\S]*>/i;
const URL_REGEX = /(https?:\/\/[^\s<]+)/g;

const linkifyTextNodes = (root: HTMLElement) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current: Node | null = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const parent = textNode.parentElement;
    if (!parent || parent.tagName.toLowerCase() === "a") return;

    const value = textNode.nodeValue || "";
    if (!URL_REGEX.test(value)) return;
    URL_REGEX.lastIndex = 0;

    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null = URL_REGEX.exec(value);

    while (match) {
      const [url] = match;
      const start = match.index;

      if (start > lastIndex) {
        frag.appendChild(document.createTextNode(value.slice(lastIndex, start)));
      }

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noreferrer noopener";
      anchor.textContent = url;
      frag.appendChild(anchor);

      lastIndex = start + url.length;
      match = URL_REGEX.exec(value);
    }

    if (lastIndex < value.length) {
      frag.appendChild(document.createTextNode(value.slice(lastIndex)));
    }

    parent.replaceChild(frag, textNode);
  });
};

const sanitizeAndEnhanceHtml = (html: string): string => {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "s",
      "del",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "blockquote",
      "pre",
      "code",
      "a",
      "img",
      "div",
      "span",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
    ],
    ALLOWED_ATTR: [
      "href",
      "src",
      "alt",
      "title",
      "width",
      "height",
      "class",
      "id",
      "style",
      "target",
      "rel",
      "colspan",
      "rowspan",
    ],
  });

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, "text/html");
  doc.querySelectorAll("a").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noreferrer noopener");
  });
  linkifyTextNodes(doc.body);
  return doc.body.innerHTML;
};

const SafeMarkdown: React.FC<{ content: string }> = ({ content }) => (
  <>
    {HTML_TAG_REGEX.test(content) ? (
      <div
        dangerouslySetInnerHTML={{ __html: sanitizeAndEnhanceHtml(content) }}
      />
    ) : (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, children, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer noopener">
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    )}
  </>
);

const AgentPublicPage: React.FC = () => {
  const { companySlug, widgetKey } = useParams<{
    companySlug: string;
    widgetKey: string;
  }>();
  const { t, i18n } = useTranslation();
  const { language, setLanguage } = useLanguageContext();

  const [page, setPage] = useState<PublicAgentPage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  // Sidebar: collapsible on every screen size. Defaults to open on desktop
  // (>= 860px) and closed on mobile so the chat gets the full viewport there.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 860px)").matches;
  });
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const [sessionId, setSessionId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const langMenuRef = useRef<HTMLDivElement | null>(null);
  const infoPopoverRef = useRef<HTMLDivElement | null>(null);
  const hasFreshGreetingRef = useRef(false);

  // -------- Page config --------
  const loadPage = useCallback(
    async (opts?: { refreshGreeting?: boolean }) => {
      if (!companySlug || !widgetKey) return;
      try {
        setIsLoading(true);
        setLoadError(null);
        const data = await publicAgentService.getAgentPage(
          companySlug,
          widgetKey,
          i18n.language
        );
        setPage(data);
        document.title = `${data.agent_name} · ${data.company.name}`;

        const storageKey = `public_session_${widgetKey}`;
        const stored = sessionStorage.getItem(storageKey);

        if (opts?.refreshGreeting && hasFreshGreetingRef.current) {
          // Language switch while on the welcome screen: refresh the
          // localized greeting only (no history to fetch).
          setMessages([{ role: "assistant", content: data.initial_message }]);
          return;
        }

        if (stored) {
          setSessionId(stored);
          try {
            const history = await publicAgentService.getChatHistory(
              widgetKey,
              stored
            );
            if (history?.messages?.length) {
              const chronological = [...history.messages].reverse();
              setMessages(
                chronological.map((m: PublicChatMessage) => ({
                  role: m.role,
                  content: m.content,
                }))
              );
              hasFreshGreetingRef.current = false;
              return;
            }
          } catch {
            /* ignore; fall through to initial greeting */
          }
        }
        hasFreshGreetingRef.current = true;
        setMessages([{ role: "assistant", content: data.initial_message }]);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setLoadError("not_found");
        } else {
          setLoadError("load_failed");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [companySlug, widgetKey, i18n.language]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadPage();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally depend on slug/key only; language changes are handled
    // by the dedicated effect below so we can swap only the greeting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, widgetKey]);

  // Note: switching the UI language only re-renders local i18n strings via
  // react-i18next. We intentionally do NOT refetch the page or re-translate
  // the agent greeting / chat history: the language picker is strictly for
  // the UI copy on this page, not a cue for the AI to answer in that tongue.

  // -------- Auto-scroll --------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // -------- Auto-resize textarea --------
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [input]);

  // -------- Outside click for lang menu --------
  useEffect(() => {
    if (!langMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!langMenuRef.current) return;
      if (!langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [langMenuOpen]);

  useEffect(() => {
    if (!infoOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!infoPopoverRef.current) return;
      if (!infoPopoverRef.current.contains(e.target as Node)) {
        setInfoOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setInfoOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [infoOpen]);

  // -------- CSS variables from widget settings --------
  const themeStyle = useMemo(() => {
    const c = getWidgetColors(page?.widget_settings);
    return {
      "--agent-primary": c.primary,
      "--agent-primary-soft": c.primarySoft,
      "--agent-primary-glow": c.primaryGlow,
      "--agent-on-primary": c.onPrimary,
      "--agent-user-bg": c.userBg,
      "--agent-user-text": c.userText,
      "--agent-bot-bg": c.botBg,
      "--agent-bot-text": c.botText,
      "--agent-input-bg": c.inputBg,
      "--agent-input-text": c.inputText,
      "--agent-input-border": c.inputBorder,
      "--agent-send-bg": c.sendBg,
      "--agent-send-text": c.sendText,
    } as React.CSSProperties;
  }, [page?.widget_settings]);

  const suggestedQuestions = useMemo(() => {
    const flows = (page as any)?.widget_settings?.flows as any[] | undefined;
    const fromFlows = Array.isArray(flows)
      ? flows
          .map((f) => f?.label || f?.title)
          .filter(Boolean)
          .slice(0, 4)
      : [];
    if (fromFlows.length) return fromFlows;
    return [
      t("public_page_quick_q_services"),
      t("public_page_quick_q_pricing"),
      t("public_page_quick_q_contact"),
    ];
  }, [page, t]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !widgetKey || isSending) return;

      const storageKey = `public_session_${widgetKey}`;
      let currentSession = sessionId;
      if (!currentSession) {
        currentSession =
          typeof crypto !== "undefined" && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setSessionId(currentSession);
        sessionStorage.setItem(storageKey, currentSession as string);
      }

      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
      setInput("");
      setIsSending(true);
      setIsTyping(true);
      hasFreshGreetingRef.current = false;

      try {
        const res = await publicAgentService.sendChatMessage(
          widgetKey,
          trimmed,
          currentSession || undefined
        );
        if (res.session_id && res.session_id !== currentSession) {
          setSessionId(res.session_id);
          sessionStorage.setItem(storageKey, res.session_id);
        }
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.message },
        ]);
      } catch (err: any) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: t("public_page_send_failed") },
        ]);
      } finally {
        setIsSending(false);
        setIsTyping(false);
      }
    },
    [widgetKey, sessionId, isSending, t]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const currentLang =
    SUPPORTED_LANGUAGES.find((l) => l.code === language) ||
    SUPPORTED_LANGUAGES[0];

  if (isLoading && !page) {
    return (
      <div className="agent-public-page agent-public-page--loading">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (loadError || !page) {
    return (
      <div className="agent-public-page agent-public-page--error">
        <div className="error-card">
          <h1>
            {loadError === "not_found"
              ? t("public_page_not_found_title")
              : t("public_page_load_failed_title")}
          </h1>
          <p>
            {loadError === "not_found"
              ? t("public_page_not_found_body")
              : t("public_page_load_failed_body")}
          </p>
        </div>
      </div>
    );
  }

  const launcherIcon = page.widget_settings?.launcher?.iconUrl;
  const showWelcome = messages.length <= 1;
  const dirAttr = i18n.dir();

  const renderAvatar = (size: "sm" | "md" | "lg" = "md") => (
    <div className={`agent-avatar agent-avatar--${size}`} aria-hidden="true">
      {launcherIcon ? (
        <img src={launcherIcon} alt="" />
      ) : (
        <span>{(page.agent_name || "A").slice(0, 1).toUpperCase()}</span>
      )}
    </div>
  );

  return (
    <div
      className={`agent-public-page${
        sidebarOpen ? " sidebar-open" : " sidebar-closed"
      }`}
      style={themeStyle}
      dir={dirAttr}
    >
      <aside
        className={`agent-public-sidebar${sidebarOpen ? " is-open" : ""}`}
        aria-hidden={!sidebarOpen}
      >
        <div className="sidebar-halo" aria-hidden="true" />

        <div className="sidebar-top">
          <div className="agent-brand">
            {renderAvatar("md")}
            <div className="agent-brand-text">
              <div className="agent-name">{page.agent_name}</div>
              {page.company?.name && (
                <div className="agent-company">{page.company.name}</div>
              )}
            </div>
          </div>

          {page.agent_description && (
            <p className="agent-description">{page.agent_description}</p>
          )}
        </div>

        <div className="sidebar-footer">
          <div className="meylo-brand">
            <a
              href="https://meylo.app"
              target="_blank"
              rel="noreferrer noopener"
              className="meylo-logo-link"
              aria-label="Meylo"
            >
              <img
                src={MEYLO_LOGO_WHITE}
                alt="Meylo"
                className="meylo-logo"
              />
            </a>
          </div>
          <div className="meylo-tagline">
            <Trans
              i18nKey="public_page_meylo_tagline"
              components={{
                brand: (
                  <a
                    href="https://www.ai-websolutions.com/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="meylo-tagline-link"
                  />
                ),
              }}
            />
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="agent-public-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className="agent-public-main">
        <div className="agent-public-main-bg" aria-hidden="true" />

        <header className="agent-public-header">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={t("public_page_toggle_sidebar")}
          >
            <span />
            <span />
            <span />
          </button>

          <div className="agent-public-header-identity">
            {renderAvatar("sm")}
            <div className="agent-public-header-text">
              <div className="agent-public-header-title">{page.agent_name}</div>
              {page.company?.name && (
                <div className="agent-public-header-subtitle">
                  {page.company.name}
                </div>
              )}
            </div>
          </div>

          <div className="agent-public-header-actions">
            <div className="lang-switcher" ref={langMenuRef}>
              <button
                type="button"
                className="lang-switcher-button"
                onClick={() => setLangMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={langMenuOpen}
                aria-label={t("language.title") || "Language"}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <span className="lang-flag">{currentLang.flag}</span>
                <span className="lang-short">{currentLang.short}</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="lang-chevron"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {langMenuOpen && (
                <div className="lang-menu" role="menu">
                  {SUPPORTED_LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      role="menuitem"
                      className={`lang-menu-item${
                        l.code === language ? " is-active" : ""
                      }`}
                      onClick={() => {
                        setLanguage(l.code);
                        setLangMenuOpen(false);
                      }}
                    >
                      <span className="lang-menu-flag">{l.flag}</span>
                      <span className="lang-menu-label">{l.label}</span>
                      {l.code === language && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="agent-public-messages">
          {showWelcome ? (
            <div className="welcome-screen">
              <div className="welcome-avatar">
                {launcherIcon ? (
                  <img src={launcherIcon} alt="" />
                ) : (
                  <span>
                    {(page.agent_name || "A").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <h1>{page.agent_name}</h1>
              <div className="message-bubble">
                <SafeMarkdown content={page.initial_message} />
              </div>
              {suggestedQuestions.length > 0 && (
                <div className="suggestion-chips">
                  {suggestedQuestions.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="suggestion-chip"
                      onClick={() => sendMessage(q)}
                      disabled={isSending}
                    >
                      <span>{q}</span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`message-row message-row--${m.role}`}
                >
                  <div className="message-avatar">
                    {m.role === "assistant" ? (
                      launcherIcon ? (
                        <img src={launcherIcon} alt="" />
                      ) : (
                        <span>
                          {(page.agent_name || "A").slice(0, 1).toUpperCase()}
                        </span>
                      )
                    ) : (
                      <span>{t("public_page_you_initial")}</span>
                    )}
                  </div>
                  <div className="message-bubble">
                    {m.role === "assistant" ? (
                      <SafeMarkdown content={m.content} />
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="message-row message-row--assistant">
                  <div className="message-avatar">
                    {launcherIcon ? (
                      <img src={launcherIcon} alt="" />
                    ) : (
                      <span>
                        {(page.agent_name || "A").slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="message-bubble typing-indicator">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </section>

        <form
          className="agent-public-composer"
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage(input);
          }}
        >
          <div className="composer-inner">
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                page.input_placeholder_text ||
                t("public_page_input_placeholder")
              }
              disabled={isSending}
              aria-label={t("public_page_input_placeholder")}
            />
            <span
              className="composer-shortcut-info"
              tabIndex={0}
              role="button"
              aria-label={t("public_page_enter_hint")}
              title={t("public_page_enter_hint")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span className="composer-shortcut-tooltip" role="tooltip">
                {t("public_page_enter_hint")}
              </span>
            </span>
            <button
              type="submit"
              className="send-button"
              disabled={isSending || !input.trim()}
              aria-label={page.send_button_text || t("public_page_send")}
            >
              {isSending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>
          </div>

          <div className="composer-info" ref={infoPopoverRef}>
            <button
              type="button"
              className={`composer-meylo-trigger${infoOpen ? " is-active" : ""}`}
              onClick={() => setInfoOpen((v) => !v)}
              aria-expanded={infoOpen}
              aria-haspopup="dialog"
              aria-label={t("public_page_about_meylo")}
              title={t("public_page_about_meylo")}
            >
              <img
                src={MEYLO_LOGO_DARK}
                alt="Meylo"
                className="composer-meylo-trigger-logo"
              />
            </button>

            {infoOpen && (
              <div
                className="composer-info-popover"
                role="dialog"
                aria-label={t("public_page_about_meylo")}
              >
                <a
                  href="https://meylo.app"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="composer-info-logo-link"
                  aria-label="Meylo"
                >
                  <img
                    src={MEYLO_LOGO_WHITE}
                    alt="Meylo"
                    className="composer-info-logo"
                  />
                </a>
                <div className="composer-info-tagline">
                  {t("public_page_meylo_platform_tagline")}
                </div>
                <a
                  href="https://meylo.co.uk"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="composer-info-cta"
                >
                  {t("public_page_meylo_discover")}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              </div>
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default AgentPublicPage;
